import { isPlatformBrowser } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
    FavIcon,
    Language,
    PageConfig,
    PageFullUrls,
    Utils,
    WebConfig,
} from 'ctt-babylon';
import { PrimeNGConfig } from 'primeng/api';
import {
    catchError,
    filter,
    map,
    Observable,
    of,
    switchMap,
    throwError,
} from 'rxjs';
import { SSR_URL } from '../../tokens/ssr-url.token';
import { ApiService, GeneralConfigResponse } from '../api/api.service';
import { LayoutService } from '../layout/layout.service';
import { UserInfoService } from '../user/user-info.service';
import { TRANSLATIONS } from './translations';

@Injectable({
    providedIn: 'root',
})
export class SiteConfigService {
    private readonly selectedLanguage = signal<Language | undefined>(undefined);
    private readonly webConfig = signal<WebConfig | undefined>(undefined);
    private readonly cdnImages = signal<string | undefined>(undefined);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly ssrUrl = inject(SSR_URL, { optional: true });

    pages?: PageFullUrls[];

    get selectedLanguageInstant(): Language | undefined {
        return this.selectedLanguage();
    }

    get webConfigInstant(): WebConfig | undefined {
        return this.webConfig();
    }

    get cdnImagesInstant(): string | undefined {
        return this.cdnImages();
    }

    constructor(
        private readonly apiService: ApiService,
        private readonly router: Router,
        private readonly primengConfig: PrimeNGConfig,
        private readonly userInfoService: UserInfoService,
        private readonly layoutSvc: LayoutService
    ) {}

    /*     changeLanguage(language: Language): void {
        this.selectedLanguage.set(language);
        this.primengConfig.setTranslation(TRANSLATIONS[language.locate]);
        document.querySelector('html')?.setAttribute('lang', language.locate);
        localStorage.setItem('language', JSON.stringify(language));
    } */

    changeLanguage(language: Language): void {
        this.selectedLanguage.set(language);
        this.primengConfig.setTranslation(TRANSLATIONS[language.locate]);

        // ✅ solo browser
        if (isPlatformBrowser(this.platformId)) {
            document
                .querySelector('html')
                ?.setAttribute('lang', language.locate);
            localStorage.setItem('language', JSON.stringify(language));
        }
    }

    setWebConfig(config: WebConfig): void {
        this.webConfig.set(config);
    }

    setCdnImages(cdn: string): void {
        this.cdnImages.set(`${cdn}/`);
    }

    getGeneralConfig(): Observable<
        HttpResponse<WebConfig> | WebConfig | undefined
    > {
        return this.apiService.getStaticConfig().pipe(
            switchMap((data) => {
                if (data) {
                    return of(data);
                }
                return this.apiService.getConfig();
            }),
            filter((data): data is GeneralConfigResponse => data !== null),
            map((data) => {
                const dataCamel = Utils.convertObjectKeysToCamelCase(data);
                this.pages = dataCamel.pages as PageFullUrls[];
                return {
                    commonScripts: dataCamel.commonScripts,
                    engine: dataCamel.engine,
                    css: dataCamel.css,
                    cdn: dataCamel.cdn,
                    favIcons: dataCamel.favicons as FavIcon[],
                    languages: dataCamel.languages,
                    logos: dataCamel.logos,
                    siteID: dataCamel.siteId,
                    projectName: dataCamel.name,
                    pages: dataCamel.pages as PageFullUrls[],
                    chatBotBtn: {
                        icon: 'icon-comment',
                    },
                    socialMedia: dataCamel.socialMedia,
                    scripts: dataCamel.scripts,
                } as WebConfig;
            })
        );
    }

    //En el futuro en el tipado de page config meteremos también los demás componenes de todas la páginas, CONTACT_COMPONENTS, GALLERY_COMPONENTS.. etc
    getPageConfig<T>(
        pageId: string | number | undefined,
        itemId?: string | number
    ): Observable<HttpResponse<PageConfig<T>> | PageConfig<T> | undefined> {
        /* if (!isPlatformBrowser(this.platformId)) {
            return of(undefined);
        } */
        this.layoutSvc.pageDataLoaded.set(false);
        if (!pageId || !this.selectedLanguageInstant?.id) {
            if (isPlatformBrowser(this.platformId)) {
                this.router.navigateByUrl('404');
            }
            if (!this.selectedLanguageInstant?.id) {
                const cfg = this.webConfigInstant;
                const fallback =
                    cfg?.languages?.find((l) => l.locate === 'es') ??
                    cfg?.languages?.[0];

                if (fallback) this.changeLanguage(fallback);
            }
        }
        const currentUrl = isPlatformBrowser(this.platformId)
            ? location.href
            : (this.ssrUrl ?? '');
        return this.userInfoService.getUserInfo().pipe(
            switchMap((userInfo) => {
                return this.apiService.getPage(
                    pageId,
                    this.selectedLanguageInstant?.id!,
                    itemId,
                    currentUrl,
                    userInfo
                );
            }),
            filter((data) => data !== null),
            map((data) => {
                const dataCamel = Utils.convertObjectKeysToCamelCase(data);
                return dataCamel as PageConfig<T>;
            }),
            catchError((err) => throwError(() => err))
        );
    }

    /*     setInitialLanguage(config: WebConfig): void {
        if (!localStorage.getItem('language')) {
            this.changeLanguage(
                (config.languages.find((lang) =>
                    navigator.language.includes(lang.locate)
                ) as Language) ??
                    config.languages.find((lang) => lang.locate === 'es')
            );
        } else {
            this.changeLanguage(
                JSON.parse(localStorage.getItem('language') ?? '')
            );
        }
        const langLocate = window.location.pathname
            .split('/')
            ?.find((item) => item.length === 2);
        if (langLocate !== this.selectedLanguageInstant?.locate && langLocate) {
            const language = config.languages.find(
                (lang) => lang.locate === langLocate
            );
            if (language) {
                this.changeLanguage(language);
            }
        }
    } */

    setInitialLanguage(config: WebConfig): void {
        // ✅ SSR: idioma desde URL
        if (!isPlatformBrowser(this.platformId)) {
            const path = this.ssrUrl ?? '/';
            const langFromUrl = path.split('/').find((x) => x?.length === 2);

            const lang =
                config.languages.find((l) => l.locate === langFromUrl) ??
                config.languages.find((l) => l.locate === 'es') ??
                config.languages[0];

            if (lang) this.changeLanguage(lang);
            return;
        }

        // ✅ Browser: tu lógica actual
        if (!localStorage.getItem('language')) {
            this.changeLanguage(
                (config.languages.find((lang) =>
                    navigator.language.includes(lang.locate)
                ) as Language) ??
                    config.languages.find((lang) => lang.locate === 'es')!
            );
        } else {
            this.changeLanguage(
                JSON.parse(localStorage.getItem('language') ?? '')
            );
        }

        const langLocate = window.location.pathname
            .split('/')
            ?.find((item) => item.length === 2);

        if (langLocate !== this.selectedLanguageInstant?.locate && langLocate) {
            const language = config.languages.find(
                (lang) => lang.locate === langLocate
            );
            if (language) this.changeLanguage(language);
        }
    }
}
